import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router";
import FullScreenLoader from "../../components/fullScreenLoader";
import { CONSTANTS } from "../../constants";
import { fetcher, headers } from "../../network";
import { API } from "../../network/api";
import { Home } from "../../types";
import { LoginHomesResponse } from "../../types/responses";
import { useHomeState } from "../../states/homeState";

const fetchHomes = async () => {
  const { data } = await fetcher().get<LoginHomesResponse>(API.GET.LOGIN_HOMES);
  return data;
};

interface LoginForm {
  home: string;
  username: string;
  password: string;
}

const loginMutation = async ({ home, password, username }: LoginForm) => {
  const { data } = await fetcher().post<{
    is2FAEnabled: boolean;
    token: string;
    home?: Home;
  }>(API.POST.LOGIN, {
    homeId: home,
    password,
    username,
  });

  return data;
};

const LoginPage: React.FC = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginForm>();

  const navigate = useNavigate();
  const setHomeId = useHomeState((state) => state.setHomeId);

  const { data, isLoading } = useQuery({
    queryKey: ["login", "homes"],
    queryFn: fetchHomes,
    staleTime: Infinity,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: loginMutation,
    onSuccess: ({ token, is2FAEnabled }, variables) => {
      headers.authorization = `Bearer ${token}`;
      setHomeId(variables.home);
      navigate(CONSTANTS.ROUTES.auth2fa, {
        state: { homeId: variables.home, is2FAEnabled },
        replace: true,
      });
    },
    onError: () => {
      toast.error("ההתחברות נכשלה");
    },
  });

  const onSubmit = (values: LoginForm) => {
    mutate(values);
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!data?.homes) {
    return null;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h2 className="mb-6 text-2xl font-bold text-center text-[#144082]">
          <span className="font-normal text-[#C64192]">מנהל</span>{" "}
          <span className="font-light">איזו</span>נפש
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Select Field */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              בחר בית
            </label>
            <select
              id="home"
              {...register("home", {
                required: "בחר בית",
              })}
              className={`w-full px-4 py-2 border rounded-md ${
                errors.home ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring focus:ring-blue-200`}
              defaultValue="">
              <option value="">בחר בית</option>
              {data.homes.map((home) => (
                <option key={home._id} value={home._id}>
                  {home.displayName}
                </option>
              ))}
            </select>
            {errors.home && (
              <p className="mt-1 text-sm text-red-500">{errors.home.message}</p>
            )}
          </div>

          {/* Username Field */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              שם משתמש
            </label>
            <input
              id="username"
              type="text"
              {...register("username", {
                required: "שדה חובה",
                minLength: {
                  value: 2,
                  message: "שם המשתמש חייב להכיל לפחות 2 תווים",
                },
              })}
              className={`w-full px-4 py-2 border rounded-md ${
                errors.username ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring focus:ring-blue-200`}
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-500">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              סיסמא
            </label>
            <input
              id="password"
              type="password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              })}
              className={`w-full px-4 py-2 border rounded-md ${
                errors.password ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring focus:ring-blue-200`}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-300">
            התחבר
          </button>
        </form>
      </div>
      <Toaster position="bottom-center" />
      {isPending && <FullScreenLoader />}
    </div>
  );
};

export default LoginPage;
